import React, { useState } from "react";
import { Send } from "lucide-react";
import { api } from "../../lib/api";
import {
  Section, TextField, TextAreaField, RadioField, CheckboxGroup,
  AcknowledgmentCheckbox, ACKNOWLEDGMENT_TEXT, Honeypot,
} from "./BookingFormFields";
import ScheduleField from "./ScheduleField";
import FileAttachmentField from "./FileAttachmentField";

const EVENT_TYPES = [
  "Birthday Celebration", "Baby Shower", "Bridal Shower", "Reception",
  "Dinner Party", "Corporate Mixer", "Networking Event",
  "Private Screening", "Meeting", "Other",
];
const SPACES = ["Lobby", "Black Box", "Full Venue"];
const STAFFING = ["Tables & Chairs", "Setup Assistance", "Cleanup Assistance", "Event Staff", "Bar Setup", "Sound Access"];
const YN = ["Yes", "No"];

const EMPTY = {
  full_name: "", email: "", phone: "", mailing_address: "", organization: "",
  event_type: "", event_description: "",
  requested_date: "", alternate_date: "",
  start_time: "", end_time: "", setup_time: "", breakdown_time: "",
  guest_count: "",
  spaces: [],
  food_served: "", food_description: "", music: "",
  outside_vendors: "", decorations: "", decorations_description: "",
  staffing_needs: [],
  atmosphere: "", budget: "", how_heard: "",
  schedule: [],
  attachments: [],
  hp_url: "",
  acknowledged: false,
};

export default function PrivateEventForm({ onSuccess, onError }) {
  const [f, setF] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acknowledged) return onError?.("Please confirm the acknowledgment to submit.");
    setSubmitting(true);
    try {
      const { full_name, email, phone, organization, hp_url, ...rest } = f;
      await api.post("/booking", {
        form_type: "private",
        full_name, email, phone, organization,
        event_title: f.event_type || "Private Event",
        hp_url,
        data: rest,
      });
      setF(EMPTY);
      onSuccess?.();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-12 relative" data-testid="private-event-form">
      <Honeypot value={f.hp_url} onChange={set("hp_url")} testId="private-hp-url" />
      <Section title="Contact Information">
        <TextField label="Full Name" value={f.full_name} onChange={set("full_name")} required testId="private-full-name" autoComplete="name" />
        <TextField label="Email Address" type="email" value={f.email} onChange={set("email")} required testId="private-email" autoComplete="email" />
        <TextField label="Phone Number" type="tel" value={f.phone} onChange={set("phone")} required testId="private-phone" autoComplete="tel" />
        <TextField label="Organization (if applicable)" value={f.organization} onChange={set("organization")} testId="private-org" autoComplete="organization" />
        <TextField label="Mailing Address" value={f.mailing_address} onChange={set("mailing_address")} fullWidth testId="private-address" />
      </Section>

      <Section title="Event Details">
        <RadioField label="Event Type" value={f.event_type} onChange={set("event_type")} options={EVENT_TYPES} required testId="private-event-type" />
        <TextAreaField label="Event Description" value={f.event_description} onChange={set("event_description")} required rows={4} testId="private-description" />
        <TextField label="Requested Date" value={f.requested_date} onChange={set("requested_date")} required testId="private-date" />
        <TextField label="Alternate Date" value={f.alternate_date} onChange={set("alternate_date")} testId="private-alt-date" />
        <TextField label="Event Start Time" value={f.start_time} onChange={set("start_time")} required testId="private-start" placeholder="e.g. 6:00 PM" />
        <TextField label="Event End Time" value={f.end_time} onChange={set("end_time")} required testId="private-end" placeholder="e.g. 10:00 PM" />
        <TextField label="Set-Up Time Needed" value={f.setup_time} onChange={set("setup_time")} testId="private-setup" />
        <TextField label="Breakdown Time Needed" value={f.breakdown_time} onChange={set("breakdown_time")} testId="private-breakdown" />
        <TextField label="Expected Guest Count" value={f.guest_count} onChange={set("guest_count")} required testId="private-guests" type="number" min={1} max={100} placeholder="Max 100" />
        <CheckboxGroup label="Which space are you interested in?" values={f.spaces} onChange={set("spaces")} options={SPACES} testId="private-spaces" />
        <ScheduleField
          label="Additional Dates / Multi-Day Schedule (optional)"
          hint="Only needed if your event spans multiple days or sessions (setup day, rehearsal, main event, etc.). Skip if a single date is enough."
          value={f.schedule}
          onChange={set("schedule")}
          testId="private-schedule"
        />
      </Section>

      <Section title="Event Logistics">
        <RadioField label="Will food be served?" value={f.food_served} onChange={set("food_served")} options={YN} required testId="private-food" />
        {f.food_served === "Yes" && (
          <TextAreaField label="What type of food will be served?" value={f.food_description} onChange={set("food_description")} required testId="private-food-description" placeholder="e.g. catered dinner, dessert table, light bites, etc." />
        )}
        <RadioField label="Will there be a DJ or live music?" value={f.music} onChange={set("music")} options={YN} required testId="private-music" />
        <RadioField label="Will outside vendors be used?" value={f.outside_vendors} onChange={set("outside_vendors")} options={YN} required testId="private-vendors" />
        <RadioField label="Will decorations be used?" value={f.decorations} onChange={set("decorations")} options={YN} required testId="private-decorations" />
        {f.decorations === "Yes" && (
          <TextAreaField label="If yes, describe" value={f.decorations_description} onChange={set("decorations_description")} testId="private-decor-desc" />
        )}
      </Section>

      <Section title="Staffing & Support">
        <CheckboxGroup label="Do you need:" values={f.staffing_needs} onChange={set("staffing_needs")} options={STAFFING} testId="private-staffing" />
      </Section>

      <Section title="Final Questions">
        <TextAreaField label="What kind of atmosphere are you hoping to create?" value={f.atmosphere} onChange={set("atmosphere")} required testId="private-atmosphere" />
        <TextField label="Estimated event budget" value={f.budget} onChange={set("budget")} required testId="private-budget" />
        <TextField label="How did you hear about Encore?" value={f.how_heard} onChange={set("how_heard")} testId="private-heard" />
        <FileAttachmentField
          label="Supporting Documents"
          hint="Optional. Floor plan, vendor list, run-of-show, or any reference docs. PDF, Word, Excel, or images. Max 5 files, 10 MB each."
          value={f.attachments}
          onChange={set("attachments")}
          testId="private-attachments"
        />
        <AcknowledgmentCheckbox checked={f.acknowledged} onChange={set("acknowledged")} testId="private-acknowledged">
          {ACKNOWLEDGMENT_TEXT}
        </AcknowledgmentCheckbox>
      </Section>

      <div className="pt-6">
        <button type="submit" disabled={submitting} data-testid="private-submit" className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3">
          {submitting ? "Sending…" : "Submit Inquiry"}
          {!submitting && <Send size={16} />}
        </button>
      </div>
    </form>
  );
}
